import { inflateRaw } from 'pako'

// Decode base64 to Uint8Array
function base64ToBytes(base64: string): Uint8Array {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192))
  }
  return btoa(binary)
}

export function decompressFlate(compressed: string | Uint8Array): string {
  try {
    if (typeof compressed === 'string') {
      const bytes = base64ToBytes(compressed)
      const decompressed = inflateRaw(bytes)
      return bytesToBase64(decompressed)
    } else {
      const decompressed = inflateRaw(compressed)
      return bytesToBase64(decompressed)
    }
  } catch (e) {
    console.error('FLATE decompression error:', e)
    return typeof compressed === 'string' ? compressed : bytesToBase64(compressed)
  }
}

interface StreamBuffer {
  audioChunks: Uint8Array[]
  scheduledEnd: number
  streaming: boolean
  lastSource: AudioBufferSourceNode | null
}

class AudioService {
  private audioContext: AudioContext | null = null
  private isPlaying = false
  private pendingAudioIds: number[] = []
  private audioMap: Record<number, string | null> = {}
  private nextAudioIdx = 0
  private streamChunkBuffers: Record<number, StreamBuffer> = {}

  resetState() {
    this.pendingAudioIds = []
    this.audioMap = {}
    this.nextAudioIdx = 0
    this.streamChunkBuffers = {}
  }

  addPendingId(id: number) {
    this.pendingAudioIds.push(id)
  }

  onAudioAnimation(id: number, audioB64: string | null) {
    this.audioMap[id] = audioB64
    this.tryPlayNext()
  }

  onStreamChunk(id: number, audioB64: string | null, done: boolean) {
    if (!this.streamChunkBuffers[id]) {
      this.streamChunkBuffers[id] = {
        audioChunks: [],
        scheduledEnd: 0,
        streaming: false,
        lastSource: null
      }
      if (!this.pendingAudioIds.includes(id)) {
        this.pendingAudioIds.push(id)
      }
    }
    const buf = this.streamChunkBuffers[id]

    if (audioB64) {
      const decompressed = decompressFlate(audioB64)
      const audioBytes = base64ToBytes(decompressed)
      if (buf.streaming) {
        this.scheduleStreamAudioBytes(id, audioBytes)
      } else if (this.pendingAudioIds[this.nextAudioIdx] === id && !this.isPlaying) {
        buf.streaming = true
        this.isPlaying = true
        this.nextAudioIdx++
        for (const chunk of buf.audioChunks) {
          this.scheduleStreamAudioBytes(id, chunk)
        }
        buf.audioChunks = []
        this.scheduleStreamAudioBytes(id, audioBytes)
      } else {
        buf.audioChunks.push(audioBytes)
      }
    }

    if (done) {
      if (buf.streaming) {
        if (buf.lastSource) {
          buf.lastSource.onended = () => {
            this.isPlaying = false
            delete this.streamChunkBuffers[id]
            this.tryPlayNext()
          }
        } else {
          this.isPlaying = false
          delete this.streamChunkBuffers[id]
          this.tryPlayNext()
        }
      } else {
        const total = buf.audioChunks.reduce((s, a) => s + a.length, 0)
        if (total > 0) {
          const combined = new Uint8Array(total)
          let off = 0
          buf.audioChunks.forEach(a => { combined.set(a, off); off += a.length })
          this.audioMap[id] = bytesToBase64(combined)
        } else {
          this.audioMap[id] = null
        }
        delete this.streamChunkBuffers[id]
        this.tryPlayNext()
      }
    }
  }

  private tryPlayNext() {
    if (this.isPlaying) return
    if (this.nextAudioIdx >= this.pendingAudioIds.length) return

    const nextId = this.pendingAudioIds[this.nextAudioIdx]
    if (!(nextId in this.audioMap)) return

    const audioData = this.audioMap[nextId]
    delete this.audioMap[nextId]
    this.nextAudioIdx++

    if (!audioData) {
      this.tryPlayNext()
      return
    }

    this.isPlaying = true
    this.playAudio(audioData)
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    }
    return this.audioContext
  }

  private scheduleStreamAudioBytes(id: number, bytes: Uint8Array) {
    if (bytes.length < 2) return
    const ctx = this.getAudioContext()
    const samples = new Float32Array(bytes.length / 2)
    for (let i = 0; i < samples.length; i++) {
      const lo = bytes[i * 2]
      const hi = bytes[i * 2 + 1]
      let sample = (hi << 8) | lo
      if (sample >= 0x8000) sample -= 0x10000
      samples[i] = sample / 32768
    }
    const buffer = ctx.createBuffer(1, samples.length, 16000)
    buffer.getChannelData(0).set(samples)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    const buf = this.streamChunkBuffers[id]
    const startTime = Math.max(ctx.currentTime, buf.scheduledEnd)
    source.start(startTime)
    buf.scheduledEnd = startTime + buffer.duration
    buf.lastSource = source
  }

  private playAudio(audioData: string) {
    try {
      const ctx = this.getAudioContext()
      const bytes = base64ToBytes(audioData)
      if (bytes.length < 2) {
        this.isPlaying = false
        this.tryPlayNext()
        return
      }
      const samples = new Float32Array(bytes.length / 2)
      for (let i = 0; i < samples.length; i++) {
        const lo = bytes[i * 2]
        const hi = bytes[i * 2 + 1]
        let sample = (hi << 8) | lo
        if (sample >= 0x8000) sample -= 0x10000
        samples[i] = sample / 32768
      }
      const buffer = ctx.createBuffer(1, samples.length, 16000)
      buffer.getChannelData(0).set(samples)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)
      source.onended = () => {
        this.isPlaying = false
        this.tryPlayNext()
      }
      source.start()
    } catch (e) {
      console.error('Audio playback error:', e)
      this.isPlaying = false
      this.tryPlayNext()
    }
  }
}

export const audioService = new AudioService()
